import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { createPixelLoader } from '../utils/pixel.utils';
import { oauthLogger } from '../utils/logging.utils';
import { AbstractTokenService } from './abstract-token.service';
import { getCachedPictureToken, removeCachedPicturePath, setCachedPictureToken } from '../utils/store.util';
import { retryWithDelay } from '../utils/operators.util';
/**
 * Service zum Laden des BA Icons.
 */
export class PixelLoaderService extends AbstractTokenService {
  constructor(loader) {
    super();
    /**
     * Zeitverzögerung für das Wiederholen beim Fehlschlag des
     * Bilder ladens
     */
    this.delayOnErrorMs = 300;
    /**
     * Bildpfade als Schlüssel im Cache, da nur das einmalige Laden eines Bildpfades instanzübergreifend notwendig ist.
     */
    this.cacheKeys = [];
    this.imageFactory = loader !== null && loader !== void 0 ? loader : createPixelLoader();
  }
  /**
   * Laden des BA Icons. Pfade stehen in der Konfiguration. Wenn mindestens ein Bild nicht geladen werden kann,
   * wird false zurückgegeben geworfen.
   * @param config - Konfiguration
   * @return Observable mit boolean
   */
  fetchToken(config) {
    const picturePaths = PixelLoaderService.getPicturesPathsByConfig(config);
    for (const pix of picturePaths) {
      if (!this.cacheKeys.includes(pix)) {
        this.cacheKeys.push(pix);
      }
    }
    return forkJoin(picturePaths.map((picture) => this.loadPictureWithRetry(picture))).pipe(map((values) => {
      return values.every((item) => item === true);
    }));
  }
  static getPicturesPathsByConfig(config) {
    var _a;
    return (_a = config === null || config === void 0 ? void 0 : config.picturePath) !== null && _a !== void 0 ? _a : [];
  }
  /**
   * Ermöglicht das Anhängen von Werten an die RequestOptions
   * @param _data
   * @param finalOptions
   * @return RequestOptions mit gesetzten Credentials
   */
  appendOptions(_data, finalOptions) {
    oauthLogger.info('Bildpfade wurden erfolgreich geladen.');
    finalOptions.credentials = 'include';
    return finalOptions;
  }
  /**
   * Lädt ein einzelnes Bild und wiederholt den Aufruf bei Bedarf.
   *
   * @param picturePath komplette URL zum zu ladenden Bild. Wird um einen Timestamp ergänzt
   * @return Returniert Observable<true>, wenn alles OK ist und Observable<false> im Fehlerfall
   */
  loadPictureWithRetry(picturePath) {
    if (picturePath == null) {
      return of(false);
    }
    const cached = getCachedPictureToken(picturePath);
    if (cached == null) {
      return of(picturePath).pipe(switchMap((path) => this.loadPicture(path)), retryWithDelay(this.delayOnErrorMs, 2));
    }
    return of(true);
  }
  /**
   * Lädt ein einzelnes Bild.
   *
   * @param picturePath komplette URL zum zu ladenden Bild. Wird um einen Timestamp ergänzt
   * @return Returniert Observable<true>, wenn alles OK ist,
   * Observable<false> wenn die Aufrufparameter nicht OK sind und einen Observable
   * Fehler im Ladefehlerfall
   */
  loadPicture(picturePath) {
    return new Observable((observer) => {
      this.imageFactory.load(picturePath, () => {
        setCachedPictureToken(picturePath, { access_token: picturePath, expires_in: 1800 });
        observer.next(true);
        observer.complete();
      }, (e) => {
        observer.error({
          e,
          message: `Fehler beim Laden des Bildes ${picturePath}`,
          name: 'PictureLoad',
        });
        observer.complete();
      });
    });
  }
  resetToken() {
    const paths = this.cacheKeys;
    paths.forEach((value) => removeCachedPicturePath(value));
    this.cacheKeys = [];
  }
  resetBy(config) {
    const savedCacheKeys = this.cacheKeys.copyWithin(this.cacheKeys.length, 0);
    this.cacheKeys = PixelLoaderService.getPicturesPathsByConfig(config);
    this.resetToken();
    this.cacheKeys = savedCacheKeys;
  }
}
//# sourceMappingURL=pixel-loader-service.js.map
